import { useSummaryProjectedFinancialsData } from "../hooks/sections/useSummaryProjectedFinancialsData";
import { useSummaryPostClosingMetricsData } from "../hooks/sections/useSummaryPostClosingMetricsData";
import { MemoTable } from "./MemoTable";
import { PeriodSelector } from "./PeriodSelector";
import { DeltaIndicator } from "./DeltaIndicator";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";
import {
    projectionsResolutionAtom,
    projectionsAsOfDateAtom,
} from "../metrics.state";
import { InfoTooltip } from "./InfoTooltip";
import { useAtomValue } from "jotai";
import { formatAsOfDate } from "../utils/formatAsOfDate";
import { useFlags } from "@/utils/flagsmith/useFlags";

function DeltaCellRenderer(params: ICellRendererParams) {
    const delta = params.value as number | null;
    return <DeltaIndicator delta={delta} />;
}

export function SummaryProjections() {
    const flags = useFlags();
    const projectedFinancialsResult = useSummaryProjectedFinancialsData();
    const postClosingMetricsResult = useSummaryPostClosingMetricsData();
    const projectionsAsOfDate = useAtomValue(projectionsAsOfDateAtom);
    const showProjectionMetrics = flags.enabled("show-projection-metrics");

    const projectedFinancialsColumns = useMemo<ColDef[]>(() => {
        return projectedFinancialsResult.columns.map((col, index) => {
            if (index === 0) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "left",
                    flex: 1.5,
                    minWidth: 140,
                    cellClass: "body-s font-medium",
                };
            }

            if (index === projectedFinancialsResult.columns.length - 1) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "right",
                    maxWidth: 140,
                    cellRenderer: DeltaCellRenderer,
                    headerComponent: (col as any).headerComponent,
                    headerComponentParams: (col as any).headerComponentParams,
                };
            }

            return {
                field: col.field,
                headerName: col.headerName,
                flex: 1,
                minWidth: 140,
            };
        });
    }, [projectedFinancialsResult.columns]);

    const postClosingMetricsColumns = useMemo<ColDef[]>(() => {
        return postClosingMetricsResult.columns.map((col, index) => {
            if (index === 0) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "left",
                    flex: 1.5,
                    minWidth: 140,
                    cellClass: "body-s font-medium",
                };
            }

            if (index === postClosingMetricsResult.columns.length - 1) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "right",
                    maxWidth: 140,
                    cellRenderer: DeltaCellRenderer,
                    headerComponent: (col as any).headerComponent,
                    headerComponentParams: (col as any).headerComponentParams,
                };
            }

            return {
                field: col.field,
                headerName: col.headerName,
                flex: 1,
                minWidth: 140,
            };
        });
    }, [postClosingMetricsResult.columns]);

    if (!showProjectionMetrics) {
        return null;
    }

    return (
        <div className="space-y-8">
            <div id="summary-projected-financials" className="space-y-3">
                <div className="flex items-start justify-between">
                    <div>
                        <h4 className="heading-m mb-2">
                            Summary Projected Financials
                        </h4>
                        <div className="body-s flex items-center gap-1">
                            <span className="subdued italic">
                                Data as of {formatAsOfDate(projectionsAsOfDate)}
                            </span>
                            <InfoTooltip>
                                Gross Margin, Sales & Marketing Efficiency and
                                Sales & Marketing spend are projected from
                                historical data using &lt;link to knowledge base
                                article&gt;ARIMA&lt;/link&gt;. Revenue is
                                derived
                            </InfoTooltip>
                        </div>
                    </div>
                    <PeriodSelector
                        resolutionAtom={projectionsResolutionAtom}
                        variant="projection"
                    />
                </div>
                <p className="body-s text-gray-400">
                    Gross Margin, Sales & Marketing Efficiency and Sales &
                    Marketing spend are projected from historical data using
                    &lt;link to knowledge base article&gt;ARIMA&lt;/link&gt;.
                    Revenue is derived by multiplying Sales & Marketing
                    Efficiency by Sales & Marketing Spend. COGS are derived by
                    multiplying Revenue by Gross Margin.
                </p>
                <MemoTable
                    data={projectedFinancialsResult.data}
                    columns={projectedFinancialsColumns}
                />
            </div>

            <div id="summary-post-closing-metrics" className="space-y-3">
                <div className="flex items-start justify-between">
                    <div>
                        <h4 className="heading-m mb-2">
                            Summary Post-Closing Metrics
                        </h4>
                        <div className="body-s flex items-center gap-1">
                            <span className="subdued italic">
                                Data as of Target Close Date
                            </span>
                            <InfoTooltip>
                                This is the target close date for this deal.
                            </InfoTooltip>
                        </div>
                    </div>
                </div>
                <MemoTable
                    data={postClosingMetricsResult.data}
                    columns={postClosingMetricsColumns}
                />
            </div>
        </div>
    );
}
